import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { Moment } from 'moment';
import { Divider, Skeleton } from 'antd';
import axios, { CancelTokenSource } from 'axios';

import { Block, Title } from '../..';
import { selectTranslations } from 'app/pages/App/slice/selectors';
import MonthPickerComponent from 'app/components/MonthPickerComponent';
import { formatNumber } from 'utils/helper';
import { getTotalRevenueApi } from 'app/services/agentApi';
import useWindowSize from 'app/hooks/useWindowSize';
import RenderIf from 'app/components/RenderIf';

interface Props {
  monthFilter: Moment;
  setMonthFilter: Dispatch<SetStateAction<Moment>>;
}

const Revenue: React.FC<Props> = ({ monthFilter, setMonthFilter }) => {
  const translations = useSelector(selectTranslations);
  const [data, setData] = useState<Record<string, any>>();
  const [isLoading, setIsLoading] = useState(true);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);
  const { device } = useWindowSize();

  useEffect(() => {
    const getRevenue = async () => {
      setIsLoading(true);
      try {
        if (cancelTokenRef.current) cancelTokenRef.current.cancel();
        cancelTokenRef.current = axios.CancelToken.source();
        const response = await getTotalRevenueApi(
          {
            type: 'business',
            date: monthFilter.format('YYYY-MM'),
          },
          { cancelToken: cancelTokenRef.current.token },
        );
        setData(response.data);
      } catch (err) {
      } finally {
        setIsLoading(false);
      }
    };
    getRevenue();

    return () => {
      if (cancelTokenRef.current) cancelTokenRef.current.cancel();
    };
  }, [monthFilter]);

  return (
    <Block>
      <FlexContainer device={device}>
        <Title>{translations?.['b2b.txt.revenue'] ?? ''}</Title>
        <MonthPickerComponent
          valueCalendar={monthFilter}
          onChangeCalendar={value => setMonthFilter(value!)}
          placement="bottomRight"
          containerStyle={{ width: device === 'mobile' ? '100%' : 'initial' }}
          style={{
            width: device === 'mobile' ? '100%' : 174,
            margin: device === 'mobile' ? '16px 0 24px' : 0,
          }}
        />
      </FlexContainer>
      <RenderIf condition={device !== 'mobile'}>
        <Divider dashed style={{ margin: '24px 0', borderColor: '#EDEDED' }} />
      </RenderIf>
      <FlexContainer
        device={device}
        style={{
          wordBreak: 'break-all',
        }}
      >
        <Text style={{ flexShrink: 0 }}>
          {translations?.['b2b.txt.totalRevenue'] ?? ''}
        </Text>
        {isLoading ? (
          <Skeleton
            paragraph={{
              rows: 1,
              style: {
                margin: 0,
                display: 'flex',
                justifyContent: device === 'mobile' ? 'flex-start' : 'flex-end',
                height: 30,
                alignItems: 'center',
              },
            }}
            title={{ style: { display: 'none' } }}
          />
        ) : (
          <RevenueValue style={{ marginLeft: device === 'mobile' ? 0 : 18 }}>
            {formatNumber(data?.totalRevenue ?? 0).replaceAll(',', '.')}đ
          </RevenueValue>
        )}
      </FlexContainer>
    </Block>
  );
};

export default Revenue;

const FlexContainer = styled.div<{ device: 'mobile' | 'tablet' | 'desktop' }>`
  display: flex;
  align-items: ${props =>
    props.device === 'mobile' ? 'flex-start' : 'center'};
  justify-content: space-between;
  flex-direction: ${props => (props.device === 'mobile' ? 'column' : 'row')};
`;

const Text = styled.p`
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #000000;
  margin: 0;
`;

const RevenueValue = styled.p`
  color: #fab317;
  font-weight: 600;
  line-height: 30px;
  font-size: 20px;
  margin: 0;
`;
